import React, { useRef, useState } from 'react';
import { Card, Button, Alert, Spinner, Table, Modal } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import {
  useGetIntegrationsFileBankAccounts,
  postIntegrationsFileBankAccountsUpload,
  deleteIntegrationsFileBankAccountsIntegrationId,
  deleteIntegrationsFileBankAccountsIntegrationIdFilesFileId,
  getGetIntegrationsFileBankAccountsQueryKey,
  useGetPayees,
  getGetPayeesQueryKey,
  putPayeesIdUser,
  getGetCommissionsStrandedBankTransactionsQueryKey,
  getGetCommissionPaymentsQueryKey,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import Select from '../../components/common/Select';
import type { FileBankAccountIntegrationSummary, Payee } from '../../api/generated/model';
import { useAllUsers } from '../../hooks/useAllUsers';
import { useNotifications } from '../../contexts/NotificationContext';

// File-uploaded bank-account integrations are autodiscovered from
// uploads — there's no manual create form. The operator just clicks
// "Upload File", picks a JSON, and the backend either creates a new
// FBAI (if the file's RoutingNumber+AccountNumber is new) or adds /
// updates the file row on an existing one.
//
// Layout: one card per FBAI (Routing+Account bucket). Each card
// shows the bucket's stats up top and a small table of uploaded
// files (File ID, File Name, transactions, last upload). Re-uploading
// a file with the same File ID overwrites that file's transactions
// in place.

function formatDate(s?: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s as string;
  return d.toLocaleString();
}

const FileBankAccountsTab: React.FC = () => {
  const queryClient = useQueryClient();
  // Mutating Payee.user_id (PUT /payees/{id}/user) is gated to
  // admin + finance on the backend. The route to System Settings is
  // already admin-gated, but other roles arriving here in future
  // should see the recipient list as read-only rather than rendering
  // a writable control they can't actually submit.
  const { user } = useSelector((state: RootState) => state.auth);
  const canEditPayeeLinks = user?.role === 'admin' || user?.role === 'finance';
  const { data, isLoading, error: loadError, refetch } = useGetIntegrationsFileBankAccounts();
  const integrations: FileBankAccountIntegrationSummary[] = (data as any)?.integrations || [];
  const { activeSyncs } = useNotifications();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FileBankAccountIntegrationSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [activeUpload, setActiveUpload] = useState<{ syncId: string; filename: string } | null>(null);

  // Per-file delete: confirmation target + active sync. The same SyncButton-
  // style "subscribe by sync_id" pattern as upload — delete runs async on
  // the shared sync queue and emits SSE events.
  const [pendingFileDelete, setPendingFileDelete] = useState<{
    integrationId: string;
    fileId: string;
    filename: string;
    routing: string;
    account: string;
  } | null>(null);
  const [activeFileDelete, setActiveFileDelete] = useState<{ syncId: string; filename: string } | null>(null);

  const liveSync = activeUpload ? activeSyncs.get(activeUpload.syncId) : undefined;
  React.useEffect(() => {
    if (!liveSync || !activeUpload) return;
    if (liveSync.status === 'completed' || liveSync.status === 'failed' || liveSync.status === 'error') {
      const t = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: getGetIntegrationsFileBankAccountsQueryKey() });
        // The upload's PASS 2.5 creates Payee(source='file') rows; the
        // recipients cross-check section reads from useGetPayees, so it
        // needs an invalidation too or it'll stay stuck on the cached
        // pre-upload result.
        queryClient.invalidateQueries({ queryKey: getGetPayeesQueryKey() });
      }, 500);
      return () => clearTimeout(t);
    }
  }, [liveSync?.status, activeUpload, queryClient]);

  const liveFileDeleteSync = activeFileDelete ? activeSyncs.get(activeFileDelete.syncId) : undefined;
  React.useEffect(() => {
    if (!liveFileDeleteSync || !activeFileDelete) return;
    if (liveFileDeleteSync.status === 'completed' || liveFileDeleteSync.status === 'failed' || liveFileDeleteSync.status === 'error') {
      const t = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: getGetIntegrationsFileBankAccountsQueryKey() });
        // delete-file's PASS 2.5 marks newly-orphaned Payee rows
        // status='deleted', which removes them from the recipients
        // cross-check; invalidate so the UI reflects that.
        queryClient.invalidateQueries({ queryKey: getGetPayeesQueryKey() });
      }, 500);
      return () => clearTimeout(t);
    }
  }, [liveFileDeleteSync?.status, activeFileDelete, queryClient]);

  const refreshList = () => {
    queryClient.invalidateQueries({ queryKey: getGetIntegrationsFileBankAccountsQueryKey() });
  };

  // Recipients cross-check: for each FBAI card we render the file-source
  // Payee rows that belong to it, with their linked dashboard user (if
  // any). Same data the payroll Manage Payees modal uses; we filter by
  // integration_id client-side. Both queries are cheap and shared with
  // other parts of the app via react-query, so the cross-check costs
  // effectively nothing extra.
  const { data: payeesData, isLoading: payeesLoading, error: payeesError } = useGetPayees({ source: 'file' });
  const filePayees: Payee[] = (payeesData as any)?.data || [];
  // useAllUsers walks /users pagination to fetch the complete user
  // list — the recipients cross-check needs every user reachable so
  // the user_id → name lookup never misses someone past the first
  // page boundary.
  const { data: users = [], isLoading: usersLoading, error: usersError } = useAllUsers();
  // If either query is loading or errored, we can't reliably show
  // linked/unlinked status — surface that explicitly instead of
  // silently rendering everything as "Unlinked".
  const recipientsDataLoading = payeesLoading || usersLoading;
  const recipientsDataError = payeesError || usersError;
  const userNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) {
      const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
      if (u.id) m.set(u.id, full || u.email || u.id);
    }
    return m;
  }, [users]);

  // Active users feed the per-recipient dropdown. Inactive users are
  // dropped so the operator can't accidentally point a payee at someone
  // who's been deactivated.
  const activeUsers = React.useMemo(
    () => users.filter(u => u.active !== false),
    [users]
  );

  // Options for the per-row link dropdown. The empty-string "Unlinked"
  // entry is at the top so picking it sends user_id=null. Built once
  // per user-list change so every row reuses the same array reference
  // (avoids react-select reflowing its width sizer on each render).
  const userOptions = React.useMemo(() => [
    { value: '', label: '— Unlinked —' },
    ...activeUsers.map(u => ({
      value: u.id || '',
      label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || u.id || '',
    })),
  ], [activeUsers]);

  // Per-recipient editing state. Selecting a different user in a row's
  // dropdown stages a pending value; clicking Save fires the PUT.
  // Saving + errors are tracked per-payee-id so multiple in-flight
  // edits in different rows don't step on each other.
  const [pendingUserByPayee, setPendingUserByPayee] = useState<Record<string, string>>({});
  const [savingPayee, setSavingPayee] = useState<string | null>(null);
  const [errorByPayee, setErrorByPayee] = useState<Record<string, string>>({});

  const handlePayeeLink = async (payeeId: string, userId: string | null) => {
    setSavingPayee(payeeId);
    setErrorByPayee(prev => {
      if (!(payeeId in prev)) return prev;
      const next = { ...prev };
      delete next[payeeId];
      return next;
    });
    try {
      await putPayeesIdUser(payeeId, { user_id: userId });
      // Backend re-derives commissions in the same transaction. Refresh
      // every consumer that surfaces linkage state: this page's payees
      // list, the stranded-BTs Issues section, and the commission_
      // payments table. Cheap belt-and-suspenders so a stale cache
      // never makes a fresh link look like it didn't take.
      queryClient.invalidateQueries({ queryKey: getGetPayeesQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetCommissionsStrandedBankTransactionsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetCommissionPaymentsQueryKey() });
      queryClient.invalidateQueries({ queryKey: ['commission-payments'] });
      setPendingUserByPayee(prev => {
        const next = { ...prev };
        delete next[payeeId];
        return next;
      });
    } catch (err: any) {
      setErrorByPayee(prev => ({
        ...prev,
        [payeeId]: err?.response?.data?.message || err?.message || 'Failed to link recipient',
      }));
    } finally {
      setSavingPayee(null);
    }
  };
  const payeesByIntegration = React.useMemo(() => {
    const m = new Map<string, Payee[]>();
    for (const p of filePayees) {
      if (p.status !== 'active') continue;
      const iid = p.integration_id || '';
      if (!iid) continue;
      const list = m.get(iid) || [];
      list.push(p);
      m.set(iid, list);
    }
    for (const list of m.values()) {
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return m;
  }, [filePayees]);

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    let parsed: any;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
    } catch (err: any) {
      setUploadError(`Failed to parse JSON: ${err?.message || err}`);
      return;
    }
    if (!parsed?.Account || !parsed?.CommissionPayments) {
      setUploadError('File is missing required Account or CommissionPayments sections.');
      return;
    }

    try {
      const resp = await postIntegrationsFileBankAccountsUpload({
        filename: file.name,
        data: parsed,
      });
      const syncId = (resp as any)?.sync_id;
      if (syncId) {
        setActiveUpload({ syncId, filename: file.name });
      } else {
        refreshList();
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || String(err);
      setUploadError(`Upload rejected: ${msg}`);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete?.id) return;
    setDeleting(true);
    try {
      await deleteIntegrationsFileBankAccountsIntegrationId(pendingDelete.id);
      setPendingDelete(null);
      refreshList();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || String(err);
      setUploadError(`Delete failed: ${msg}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmFileDelete = async () => {
    if (!pendingFileDelete) return;
    const { integrationId, fileId, filename } = pendingFileDelete;
    setPendingFileDelete(null);
    try {
      const resp = await deleteIntegrationsFileBankAccountsIntegrationIdFilesFileId(integrationId, fileId);
      const syncId = (resp as any)?.sync_id;
      if (syncId) {
        setActiveFileDelete({ syncId, filename });
      } else {
        refreshList();
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || String(err);
      setUploadError(`Delete failed: ${msg}`);
    }
  };

  return (
    <Card>
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <div className="me-4 bg-header-control rounded p-2 d-flex align-items-center justify-content-center" style={{ width: '4.5rem', height: '4.5rem' }}>
              <i className="bi bi-file-earmark-bar-graph fs-1 text-primary" />
            </div>
            <div>
              <h5 className="mb-1">File Bank Accounts</h5>
              <p className="text-muted mb-0">
                Bank accounts whose transactions are uploaded as JSON files (mercurydb FileAccountData format).
                Integrations are auto-discovered from uploads — no manual setup needed.
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              onChange={handleFileChosen}
            />
            <Button
              variant="outline-primary"
              onClick={handlePickFile}
              disabled={!!activeUpload && !!liveSync && (liveSync.status === 'started' || liveSync.status === 'progress')}
            >
              <i className="bi bi-upload me-1" />Upload File
            </Button>
          </div>
        </div>

        {uploadError && (
          <Alert variant="danger" dismissible onClose={() => setUploadError(null)} className="mb-3">
            {uploadError}
          </Alert>
        )}

        {activeUpload && liveSync && (liveSync.status === 'started' || liveSync.status === 'progress') && (
          <Alert variant="info" className="mb-3">
            <Spinner size="sm" animation="border" className="me-2" />
            Uploading <code>{activeUpload.filename}</code> — {liveSync.progress?.message || 'in progress…'}
          </Alert>
        )}
        {activeUpload && liveSync && liveSync.status === 'completed' && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setActiveUpload(null)}
            className="mb-3"
          >
            <code>{activeUpload.filename}</code> uploaded successfully.
            {liveSync.result && (
              <span className="ms-2 text-muted small">
                created {liveSync.result.created || 0}, updated {liveSync.result.updated || 0}, deleted {liveSync.result.deleted || 0}
                {liveSync.result.errors ? `, ${liveSync.result.errors} warnings` : ''}
              </span>
            )}
          </Alert>
        )}
        {activeUpload && liveSync && (liveSync.status === 'failed' || liveSync.status === 'error') && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setActiveUpload(null)}
            className="mb-3"
          >
            Upload failed: {liveSync.error || 'unknown error'}
          </Alert>
        )}

        {activeFileDelete && liveFileDeleteSync && (liveFileDeleteSync.status === 'started' || liveFileDeleteSync.status === 'progress') && (
          <Alert variant="info" className="mb-3">
            <Spinner size="sm" animation="border" className="me-2" />
            Deleting <code>{activeFileDelete.filename}</code> — {liveFileDeleteSync.progress?.message || 'in progress…'}
          </Alert>
        )}
        {activeFileDelete && liveFileDeleteSync && liveFileDeleteSync.status === 'completed' && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setActiveFileDelete(null)}
            className="mb-3"
          >
            <code>{activeFileDelete.filename}</code> deleted.
            {liveFileDeleteSync.result && (
              <span className="ms-2 text-muted small">
                removed {liveFileDeleteSync.result.deleted || 0} transactions
                {liveFileDeleteSync.result.errors ? `, ${liveFileDeleteSync.result.errors} warnings` : ''}
              </span>
            )}
          </Alert>
        )}
        {activeFileDelete && liveFileDeleteSync && (liveFileDeleteSync.status === 'failed' || liveFileDeleteSync.status === 'error') && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setActiveFileDelete(null)}
            className="mb-3"
          >
            Delete failed: {liveFileDeleteSync.error || 'unknown error'}
          </Alert>
        )}

        {loadError && (
          <Alert variant="warning" className="mb-3">
            Failed to load integrations: {String((loadError as any)?.message || loadError)}{' '}
            <Button size="sm" variant="link" onClick={() => refetch()}>retry</Button>
          </Alert>
        )}

        {isLoading && integrations.length === 0 ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2 text-muted">Loading integrations…</p>
          </div>
        ) : integrations.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-cloud-upload fs-1 d-block mb-2" />
            <div>No file bank accounts yet.</div>
            <div className="small">Upload a commissions JSON file to auto-discover one.</div>
          </div>
        ) : (
          <div className="row">
            {integrations.map((row) => {
              const id = row.id || '';
              const files = row.files || [];
              const recipients = payeesByIntegration.get(id) || [];
              const linkedCount = recipients.filter(p => !!p.user_id).length;
              return (
                <div key={id} className="col-12 col-xxl-6 mb-3">
                  <Card className="border h-100">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            <i className="bi bi-bank me-2 text-primary" />
                            <code>{row.routing_number}</code>
                            <span className="text-muted mx-2">/</span>
                            <code>{row.account_number}</code>
                          </h6>
                        </div>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => setPendingDelete(row)}
                          title="Remove this integration and all of its data"
                        >
                          <i className="bi bi-trash" />
                        </Button>
                      </div>

                      <div className="row g-2 mb-3 small text-muted">
                        <div className="col-4">
                          <div className="fw-semibold text-body">{row.file_count ?? 0}</div>
                          <div>Files</div>
                        </div>
                        <div className="col-4">
                          <div className="fw-semibold text-body">{row.transaction_count ?? 0}</div>
                          <div>Transactions</div>
                        </div>
                        <div className="col-4">
                          <div className="fw-semibold text-body">{formatDate(row.last_upload_at)}</div>
                          <div>Last Upload</div>
                        </div>
                      </div>

                      {files.length === 0 ? (
                        <Alert variant="secondary" className="mb-0 py-2 small">
                          No files yet — upload one to populate this account.
                        </Alert>
                      ) : (
                        <div className="border rounded table-responsive">
                          <Table size="sm" hover className="mb-0 align-middle">
                            <thead className="table-light">
                              <tr>
                                <th>File ID</th>
                                <th>File Name</th>
                                <th>Uploaded By</th>
                                <th className="text-end">Txns</th>
                                <th>Last Upload</th>
                                <th style={{ width: '2.5rem' }} />
                              </tr>
                            </thead>
                            <tbody>
                              {files.map((f) => (
                                <tr key={f.id}>
                                  <td><code className="small">{f.id}</code></td>
                                  <td className="small">{f.filename}</td>
                                  <td className="small">{f.uploaded_by_user_name || f.uploaded_by_user_id}</td>
                                  <td className="text-end small">{f.transaction_count ?? 0}</td>
                                  <td className="small">{formatDate(f.updated_at)}</td>
                                  <td className="text-end">
                                    <Button
                                      size="sm"
                                      variant="link"
                                      className="text-danger p-0"
                                      title="Delete this file"
                                      onClick={() => setPendingFileDelete({
                                        integrationId: id,
                                        fileId: f.id || '',
                                        filename: f.filename || f.id || '',
                                        routing: row.routing_number || '',
                                        account: row.account_number || '',
                                      })}
                                    >
                                      <i className="bi bi-trash" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}

                      {/* Recipients cross-check: every distinct counterparty seen
                          in this integration's files, with its dashboard-user
                          link status. Operators link/unlink via the existing
                          payroll Manage Payees modal. */}
                      <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-1 small text-muted">
                          <span>
                            <i className="bi bi-people me-1" />
                            Recipients
                          </span>
                          <span>
                            {recipientsDataLoading
                              ? 'loading…'
                              : recipientsDataError
                                ? 'unavailable'
                                : recipients.length === 0
                                  ? 'none'
                                  : `${linkedCount}/${recipients.length} linked`}
                          </span>
                        </div>
                        {recipientsDataError ? (
                          <Alert variant="warning" className="mb-0 py-2 small">
                            <i className="bi bi-exclamation-triangle me-1" />
                            Unable to load link status: {String((recipientsDataError as any)?.message || recipientsDataError)}
                          </Alert>
                        ) : recipientsDataLoading ? (
                          <div className="small text-muted fst-italic">
                            <Spinner size="sm" animation="border" className="me-2" />
                            Loading recipients…
                          </div>
                        ) : recipients.length === 0 ? (
                          <div className="small text-muted fst-italic">
                            No recipients yet — upload a file to populate.
                          </div>
                        ) : (
                          <div className="border rounded table-responsive">
                            <Table size="sm" hover className="mb-0 align-middle">
                              <thead className="table-light">
                                <tr>
                                  {/* width:1% + whiteSpace:nowrap is the
                                      canonical CSS trick for "shrink to
                                      natural content width". Without it
                                      auto-layout reapportions columns
                                      whenever the Dashboard User cell's
                                      content varies (Save button
                                      appearing/disappearing), pulling
                                      every dropdown leftward and back. */}
                                  <th style={{ whiteSpace: 'nowrap', width: '1%' }}>Recipient</th>
                                  <th>Dashboard User</th>
                                </tr>
                              </thead>
                              <tbody>
                                {recipients.map((p) => {
                                  const linkedName = p.user_id ? userNameById.get(p.user_id) : null;
                                  // p.user_id set but no matching user in our
                                  // map = data inconsistency (deleted user, or
                                  // a paginated /users response we didn't fully
                                  // walk). Surface explicitly — silently
                                  // rendering as "Unlinked" hides the bug.
                                  const orphanUserID = !!p.user_id && !linkedName;
                                  // Pending value falls back to the row's
                                  // current user_id, so the dropdown shows
                                  // the existing link out-of-the-gate. Save
                                  // is enabled only when the operator's
                                  // chosen value differs.
                                  const payeeId = p.id || '';
                                  const currentUserId = p.user_id || '';
                                  const pending = (payeeId in pendingUserByPayee)
                                    ? pendingUserByPayee[payeeId]
                                    : currentUserId;
                                  const isSaving = savingPayee === payeeId;
                                  const dirty = pending !== currentUserId;
                                  const rowErr = errorByPayee[payeeId];
                                  return (
                                    <tr key={p.id}>
                                      {/* nowrap matches the th — without it
                                          the cell still wraps even though the
                                          column tries to shrink to natural
                                          content width. */}
                                      <td className="small text-nowrap">{p.name}</td>
                                      <td className="small">
                                        {!canEditPayeeLinks ? (
                                          // Read-only branch — non-admin/finance
                                          // viewers see the original status
                                          // readout, no writable control.
                                          linkedName ? (
                                            <span>
                                              <i className="bi bi-link-45deg text-success me-1" />
                                              {linkedName}
                                            </span>
                                          ) : orphanUserID ? (
                                            <span className="text-danger" title={`user_id ${p.user_id} not found in users response`}>
                                              <i className="bi bi-question-circle me-1" />
                                              Unknown user ({p.user_id})
                                            </span>
                                          ) : (
                                            <span className="text-warning">
                                              <i className="bi bi-exclamation-triangle me-1" />
                                              Unlinked
                                            </span>
                                          )
                                        ) : (
                                        <div className="d-flex flex-column gap-1">
                                          <div className="d-flex gap-2 align-items-center flex-wrap">
                                            <Select
                                              options={
                                                // Append the orphan user_id as a
                                                // disabled option so the operator
                                                // sees the broken state instead
                                                // of a silent "— Unlinked —"
                                                // — and can pick a real user to
                                                // overwrite it.
                                                orphanUserID
                                                  ? [...userOptions, { value: currentUserId, label: `Unknown user (${p.user_id})`, isDisabled: true } as any]
                                                  : userOptions
                                              }
                                              value={
                                                pending === ''
                                                  ? userOptions[0]
                                                  : userOptions.find(o => o.value === pending)
                                                    || (orphanUserID ? { value: currentUserId, label: `Unknown user (${p.user_id})`, isDisabled: true } as any : null)
                                              }
                                              onChange={(opt: any) => {
                                                const v = (opt && opt.value) || '';
                                                setPendingUserByPayee(prev => ({ ...prev, [payeeId]: v }));
                                              }}
                                              isDisabled={isSaving}
                                              placeholder="Pick user…"
                                              menuPortalTarget={document.body}
                                              styles={{
                                                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                                                // Pin the container's flex
                                                // sizing to "shrink-wrap to
                                                // content". Default is flex
                                                // 0 1 auto, which lets parent
                                                // layout changes (Save button
                                                // appearing/disappearing,
                                                // sibling badge swapping
                                                // widths) shift the control's
                                                // position within the cell.
                                                container: (base: any) => ({ ...base, flex: '0 0 auto' }),
                                              }}
                                            />
                                            {dirty && (
                                              <>
                                                <Button
                                                  size="sm"
                                                  variant="primary"
                                                  disabled={isSaving}
                                                  onClick={() => handlePayeeLink(payeeId, pending || null)}
                                                >
                                                  {isSaving ? (<><Spinner size="sm" animation="border" className="me-1" />…</>) : 'Save'}
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  variant="outline-secondary"
                                                  disabled={isSaving}
                                                  onClick={() => setPendingUserByPayee(prev => {
                                                    if (!(payeeId in prev)) return prev;
                                                    const next = { ...prev };
                                                    delete next[payeeId];
                                                    return next;
                                                  })}
                                                  title="Discard staged change and revert to current value"
                                                >
                                                  Cancel
                                                </Button>
                                              </>
                                            )}
                                            {!dirty && !linkedName && !orphanUserID && (
                                              <span className="text-warning small text-nowrap">
                                                <i className="bi bi-exclamation-triangle me-1" />
                                                Unlinked
                                              </span>
                                            )}
                                          </div>
                                          {rowErr && (
                                            <div className="text-danger small">
                                              <i className="bi bi-exclamation-triangle-fill me-1" />
                                              {rowErr}
                                            </div>
                                          )}
                                        </div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>

      <Modal show={!!pendingDelete} onHide={() => setPendingDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete File Bank Account?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Delete the file bank account for routing <code>{pendingDelete?.routing_number}</code>,
            account <code>{pendingDelete?.account_number}</code>?
          </p>
          <p className="text-muted small mb-0">
            This removes every uploaded file, every transaction recorded under this integration, and every
            commission payment derived from those transactions. Re-uploading any file with the same
            RoutingNumber+AccountNumber will auto-discover a fresh one.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPendingDelete(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? <><Spinner size="sm" animation="border" className="me-1" />Deleting…</> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!pendingFileDelete} onHide={() => setPendingFileDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete File?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Delete <code>{pendingFileDelete?.filename}</code> from the bucket{' '}
            <code>{pendingFileDelete?.routing}</code>/<code>{pendingFileDelete?.account}</code>?
          </p>
          <p className="text-muted small mb-0">
            Wipes the file's transactions, the bank transactions derived from them, and any commission
            payments anchored to those bank transactions. The integration itself stays — re-upload the same
            File ID later to bring its data back.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPendingFileDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmFileDelete}>
            Delete File
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default FileBankAccountsTab;
